import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useGenerateReport } from '../../../api/documents';
import { isApiError } from '../../../api/errors';
import { useInspectionForms } from '../../../api/forms';
import type { Inspection } from '../../../api/inspections';
import { useEnumLabel } from '../../../api/enums';
import { Button, ConfirmDialog, WarningText } from '../../../components/idsk';
import { toast } from '../../../components/common/toasts';

/** The confirmation step of report generation; on confirm it generates the report (which finishes the inspection). */
export function ReportConfirmDialog({ inspection, open, onClose, onError }: { inspection: Inspection; open: boolean; onClose: () => void; onError: (text: string) => void }) {
  const { t } = useTranslation();
  const generate = useGenerateReport(inspection.id);

  const run = async () => {
    onClose();
    try {
      await generate.mutateAsync(inspection.version);
      toast.success(t('report.generated', { number: inspection.number }));
    } catch (e) {
      onError(isApiError(e) ? (e.isVersionConflict ? t('errors.versionConflict') : e.message) : t('errors.unexpected'));
    }
  };

  return (
    <ConfirmDialog open={open} title={t('report.confirm.title')} confirmText={t('report.confirm.confirm')} onCancel={onClose} onConfirm={() => void run()}>
      <WarningText>{t('report.confirm.warning')}</WarningText>
      <p className="govuk-body">{t('report.confirm.body', { number: inspection.number })}</p>
    </ConfirmDialog>
  );
}

/**
 * "Vygenerovať správu" for ADMIN, only in EVALUATION: a confirmation step first, because the inspection becomes
 * read-only. Requires a complete evaluation form (the backend enforces the same rule). The leader uses "Hotovo".
 */
export function ReportAction({ inspection, onError }: { inspection: Inspection; onError: (text: string) => void }) {
  const { t } = useTranslation();
  const label = useEnumLabel();
  const forms = useInspectionForms(inspection.id);
  const [confirm, setConfirm] = useState(false);
  const evaluation = forms.data?.find((f) => f.formType === 'EVALUATION');
  const complete = evaluation?.complete === true;

  return (
    <div className="govuk-!-margin-bottom-6">
      <h3 className="govuk-heading-s">{t('report.heading')}</h3>
      {!forms.data ? (
        <p className="govuk-body">{t('common.loading')}</p>
      ) : !complete ? (
        <p className="govuk-body">
          {t('report.completeFirst', { form: label('formTypes', 'EVALUATION') })}{' '}
          <Link className="govuk-link" to={`/inspekcie/${inspection.id}/formulare/EVALUATION`}>
            {t('workflow.openForm')}
          </Link>
        </p>
      ) : (
        <>
          <p className="govuk-body">{t('report.intro')}</p>
          <Button onClick={() => setConfirm(true)}>{t('report.generate')}</Button>
        </>
      )}
      <ReportConfirmDialog inspection={inspection} open={confirm} onClose={() => setConfirm(false)} onError={onError} />
    </div>
  );
}
